import { __assign, __rest } from "tslib";
import React, { useCallback, useContext, useEffect, useRef, useState, } from 'react';
import c from 'classnames';
import ReactModal from 'react-modal';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeOverride, ThemeOptions, } from '@wix/da-react-context/pkg/ThemeContext';
import { useBodyScrollLock, } from '../../../hooks/useBodyScrollLock';
import { useVisualViewport } from '../../../hooks/useVisualViewport';
import IconButton from '../../Buttons/IconButton';
import Close from '../../Icons/24x24/Close';
import { PopupContext } from '../../popups/PopupContext';
import s from './ModalRoot.scss';
/**
 * The root component of any modal. This is a wrapper for `react-popper`'s `<ReactPopper />` component.
 *
 * Provides theming, body scroll locking, and some additional styling.
 */
var ModalRoot = function (_a) {
    var className = _a.className, width = _a.width, height = _a.height, useFixedMobileHeight = _a.useFixedMobileHeight, closeButtonOutside = _a.closeButtonOutside, bodyScrollLockOptions = _a.bodyScrollLockOptions, onRequestClose = _a.onRequestClose, children = _a.children, innerContentRef = _a.innerContentRef, rest = __rest(_a, ["className", "width", "height", "useFixedMobileHeight", "closeButtonOutside", "bodyScrollLockOptions", "onRequestClose", "children", "innerContentRef"]);
    var isMobile = useContext(MobileContext);
    // Mobile fixed to viewport fixes
    var _b = useState(), visualViewport = _b[0], setVisualViewport = _b[1];
    var viewportHandler = useCallback(function (v) {
        setVisualViewport(v);
    }, []);
    useVisualViewport(viewportHandler, isMobile && !!useFixedMobileHeight);
    var style = {
        content: {
            width: width,
            height: height,
        },
    };
    var contentStyle = {};
    // Apply fixed height for mobile if requested, helper for fixing Safari being lame
    if (isMobile && visualViewport && !height) {
        style.content.height = visualViewport.height;
        style.content.transform = "translate(".concat(visualViewport.offsetLeft, "px,").concat(visualViewport.offsetTop, "px)");
        style.content.scale = visualViewport.scale;
        // Calculate positions from top left
        style.content.top = 0;
        style.content.left = 0;
        style.content.position = 'fixed';
        // Set the max height of the content, fixes content expanding beyond viewport
        contentStyle.maxHeight = visualViewport.height;
    }
    // Mobile body scroll locking - Important makes IOS scroll
    var contentRef = useRef(null);
    var _c = useBodyScrollLock(bodyScrollLockOptions), onLock = _c.onLock, onUnlock = _c.onUnlock, setScrollableRef = _c.setScrollableRef, scrollableElementRef = _c.scrollableElementRef;
    useEffect(function () {
        var focusedEl = document.activeElement;
        var contentEl = contentRef.current;
        var isFocused = function (el) { return el === focusedEl || el.contains(focusedEl); };
        var needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
        var focusTimeout;
        if (needsFocus) {
            // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
            focusTimeout = setTimeout(function () { return contentEl.focus(); }, 100);
        }
        return function () { return focusTimeout && clearTimeout(focusTimeout); };
    }, []);
    var setContentRef = function (instance) {
        contentRef.current = instance;
        // Important makes IOS scroll
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        // This is actually the fallback, in general you have ModalLayout which will set the default scrollale below.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
            innerContentRef && innerContentRef(instance);
        }
    };
    var setDefaultScrollRef = function (instance) {
        // Important makes IOS scroll
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        // This is the default when using ModalLayout, there is a fallback above which is the the ReactModal content.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
            innerContentRef && innerContentRef(instance);
        }
    };
    var handleClose = useCallback(function (e) {
        onUnlock();
        onRequestClose && onRequestClose(e);
    }, [onRequestClose, onUnlock]);
    var closeButton = (React.createElement(IconButton, { className: c(closeButtonOutside && s['close-button-outside'], s['close-button']), size: "large", icon: Close, onClick: handleClose }));
    return (React.createElement(ReactModal, __assign({ className: c(s['root'], className), overlayClassName: s['overlay'], portalClassName: 'ReactModalPortal', style: style, contentRef: setContentRef, onAfterOpen: onLock, onRequestClose: handleClose }, rest),
        React.createElement(ThemeOverride, { theme: "match", className: s['theme-wrapper'] },
            React.createElement(PopupContext.Provider, { value: {
                    isInPopup: true,
                    setScrollableContentRef: setScrollableRef,
                    setDefaultScrollableRef: setDefaultScrollRef,
                } }, children)),
        closeButtonOutside ? (React.createElement(ThemeOverride, { theme: ThemeOptions.DARK }, closeButton)) : (closeButton)));
};
ModalRoot.displayName = 'ModalRoot';
export default ModalRoot;
//# sourceMappingURL=ModalRoot.js.map